import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Textarea } from "../../shared/ui";
import type { ReopenView, TimesheetView } from "../../shared/types/api";
import { formatHoursAsTime, formatPeriod } from "./timesheetVocabulary";

/**
 * §78: asking for a settled period to be reopened.
 *
 * <p>The reason is required, and the panel says why before the field rather than after a
 * rejection: a correction to an approved month may already have been paid or invoiced, so the
 * request has to carry enough for somebody to decide on without asking.
 */
export function RequestCorrectionDrawer({
  open,
  timesheet,
  onClose,
  onSaved,
}: {
  open: boolean;
  timesheet: TimesheetView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    if (reason.trim() === "") {
      setError("Say what needs correcting.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/timesheets/${timesheet.id}/request-reopen`, { reason });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not send that request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Request a correction"
      subtitle={formatPeriod(timesheet.periodStart, timesheet.periodEnd)}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? "Sending…" : "Send request"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        <InlineAlert tone="info">
          This period is closed, so the time in it cannot be changed until somebody reopens it.
          Approving your request puts it back to draft — the entries are not altered for you.
        </InlineAlert>
        <Field
          label="What needs correcting"
          required
          hint="Which day, and what it should say. Whoever decides has only these words to go on."
        >
          <Textarea
            rows={5}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Tuesday the 10th is logged against Alpha; it was Beta."
          />
        </Field>
      </div>
    </Drawer>
  );
}

/**
 * Approving or sending back a submitted period (§76).
 *
 * <p>Approval closes the period to editing, so the panel says so plainly. Sending back requires a
 * reason: "rejected" with no explanation leaves somebody re-reading a whole month with no idea
 * what they are looking for.
 */
export function DecisionDrawer({
  open,
  decision,
  timesheet,
  onClose,
  onSaved,
}: {
  open: boolean;
  decision: "approve" | "reject";
  timesheet: TimesheetView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const rejecting = decision === "reject";

  useEffect(() => {
    if (open) {
      setNote("");
      setError(null);
    }
  }, [open, decision]);

  const submit = async () => {
    if (rejecting && note.trim() === "") {
      setError("Say what needs putting right.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(
        `/api/v1/timesheets/${timesheet.id}/${rejecting ? "reject" : "approve"}`,
        rejecting ? { reason: note } : { note: note || null },
      );
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not record that decision.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={rejecting ? "Send this period back" : "Approve this period"}
      subtitle={`${timesheet.employeeName} · ${formatPeriod(
        timesheet.periodStart,
        timesheet.periodEnd,
      )}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant={rejecting ? "danger" : "primary"} onClick={submit} disabled={saving}>
            {saving ? "Saving…" : rejecting ? "Send back" : "Approve"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-ink-50 px-3 py-2">
            <p className="text-xs text-ink-500">Expected</p>
            <p className="tnum font-medium text-ink-900">
              {formatHoursAsTime(timesheet.expectation.expectedHours)}
            </p>
          </div>
          <div className="rounded-lg bg-ink-50 px-3 py-2">
            <p className="text-xs text-ink-500">Logged</p>
            <p className="tnum font-medium text-ink-900">
              {formatHoursAsTime(timesheet.expectation.loggedHours)}
            </p>
          </div>
        </div>

        {!rejecting && (
          <InlineAlert tone="info">
            Approving closes the period. Nobody can add, change or remove time in these dates
            afterwards without a correction being requested and granted.
          </InlineAlert>
        )}

        <Field
          label={rejecting ? "What needs putting right" : "Note"}
          required={rejecting}
          hint={
            rejecting
              ? "This is all the person will see, so name the days or entries."
              : "Optional, and kept with the period."
          }
        >
          <Textarea rows={4} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}

/** Deciding a §78 correction request: reopening returns the period to draft. */
export function ReopenDecisionDrawer({
  open,
  request,
  decision,
  onClose,
  onSaved,
}: {
  open: boolean;
  request: ReopenView | null;
  decision: "approve" | "reject";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setError(null);
    }
  }, [open, decision]);

  const submit = async () => {
    if (!request) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(
        `/api/v1/timesheets/reopen-requests/${request.id}/${
          decision === "approve" ? "approve" : "reject"
        }`,
        { note: note || null },
      );
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not record that decision.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={decision === "approve" ? "Reopen this period" : "Decline this request"}
      subtitle={
        request ? `${request.employeeName} · ${formatPeriod(request.periodStart, request.periodEnd)}` : undefined
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant={decision === "approve" ? "primary" : "danger"}
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Saving…" : decision === "approve" ? "Reopen" : "Decline"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        {request && (
          <div className="rounded-lg border border-ink-200 px-3 py-2.5">
            <p className="text-xs text-ink-500">Their reason</p>
            <p className="mt-0.5 text-sm text-ink-800">{request.reason}</p>
          </div>
        )}
        {decision === "approve" && (
          <InlineAlert tone="warning">
            The period goes back to draft and its time becomes editable again. It will need
            submitting and approving a second time.
          </InlineAlert>
        )}
        <Field label="Note" hint="Kept with the request, and visible to the person who asked.">
          <Textarea rows={4} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}
